/**
 * AppShell Component
 *
 * Main layout with responsive panels using react-resizable-panels (desktop)
 * OR a single-pane stacked tab switcher (touch / mobile).
 *
 * 1.127.0 Phase 2d: branches between DesktopPanelGroup and
 * MobileStackedShell based on useIsTouch() (matchMedia pointer: coarse).
 * Desktop layout is byte-identical to pre-Phase-2d — the existing
 * PanelGroup tree is extracted into a local helper unchanged.
 *
 * Mount discipline: each panel JSX element is materialized once in
 * AppShell.render and passed by prop to whichever shell renders. Across
 * an isTouch flip (DevTools "Toggle device toolbar" only — real devices
 * never flip), React WILL remount the panels because parent component
 * type changes. Acceptable per Phase 2d plan §9 risk 1 + §5.6.
 *
 * Desktop layout: Files | (Editor / Preview) + Info | Chat
 * Mobile layout: top tab-bar + 4 single-pane stack (Chat / Editor /
 *   Preview / Info). Default tab: Chat. Files is in a dedicated
 *   FilesDrawer mounted as a sibling (Phase 2e — opens from a button
 *   on the LEFT of the tab bar, outside the role="tablist" container).
 *
 * Panel sizes are percentages. Minimum viewport: 1024x768 (desktop).
 */
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelHandle,
} from 'react-resizable-panels';
import { useEffect, useRef } from 'react';
import { ResizableVoicePanel } from '../voice/ResizableVoicePanel';
import { HelpOverlay } from '@/components/help/HelpOverlay';
import { useHelpStore } from '@/stores/helpStore';
import { FilesPanel } from './FilesPanel';
import { EditorPanel } from './EditorPanel';
import { PreviewPanel } from './PreviewPanel';
import { ChatPanel } from './ChatPanel';
import { InfoPanel } from './InfoPanel';
import { MobileStackedShell } from './MobileStackedShell';
import { FilesDrawer } from './FilesDrawer';
import { useIsTouch } from '@/hooks/useIsTouch';
import { useProjectTreeAutoRefresh } from '@/hooks/useProjectTreeAutoRefresh';
import { useProjectStore } from '@/stores/projectStore';
import { useUIStore } from '@/stores/uiStore';
import { useSpaContextStore } from '@/stores/spaContextStore';
import { buildDesignerSnapshot } from './designerSnapshot';

// Panel configuration (percentages)
// At 1024px min viewport:
// - Files: 15% = ~154px (min 11% = ~112px, max 22% = ~225px)
// - Center: 85% = ~870px (min 40% = ~410px)
// "Pi chat on all tabs" P1 (2026-07-03): the chat Panel (was 28%) is hoisted
// OUT of the desktop group into PiChatDock (an App-level overlay drawer present
// on every view). Center absorbs the freed width so files + center re-sum to
// 100. Mobile keeps the chat pane (see MobileStackedShell) — dock is desktop-only.
const PANELS = {
  files: { default: 15, min: 11, max: 22 },
  center: { default: 85, min: 40 },
  editorPreview: { default: 75, min: 50 },
  info: { default: 25, min: 15, max: 50 },
  editor: { default: 50, min: 30 },
  preview: { default: 50, min: 30 },
};

function ResizeHandle({ direction = 'vertical' }: { direction?: 'vertical' | 'horizontal' }) {
  const isVertical = direction === 'vertical';
  return (
    <PanelResizeHandle
      className={`group relative flex items-center justify-center ${isVertical ? '' : 'h-1'}`}
    >
      <div
        className={`${isVertical ? 'w-1 h-full' : 'h-1 w-full'} bg-vibe-border group-hover:bg-vibe-accent/50 group-active:bg-vibe-accent transition-colors`}
      />
    </PanelResizeHandle>
  );
}

export function AppShell() {
  // NOTE: do NOT remount the panel tree on window resize. react-resizable-panels
  // re-lays-out natively on container resize (internal ResizeObserver) with
  // percentage sizes, so a `key={`main-${resizeCount}`}` churn is redundant —
  // and it was DESTROYING the in-progress Pi <pi-chat-panel> web component
  // mid-stream, dropping the most recent chat messages. (Removed 2026-06-28.)
  // 1.127.0 Phase 2d: matchMedia('(pointer: coarse)') cached in uiStore.
  // Production: locked at mount, never flips. DevTools toggle: causes
  // remount of all 5 panels because parent component type changes.
  const isTouch = useIsTouch();

  // Issue 5 (2026-07-14) — keep the Explorer tree fresh when files change
  // outside the SPA (agent writes / external git pushes): ~30s stale-poll +
  // return-to-foreground refresh. Self-contained (reads currentProject from the
  // store); no-op until a project is open.
  useProjectTreeAutoRefresh();

  // 2026-06-27 — first-run guided tour. Auto-start the help tour the very
  // first time a user opens Vibe (helpStore persists a "seen" flag in
  // localStorage). Desktop only — the tour anchors are desktop-chrome
  // elements. The "?" Help button replays it any time.
  const startTour = useHelpStore((s) => s.startTour);
  const hasSeenTour = useHelpStore((s) => s.hasSeenTour);
  useEffect(() => {
    if (isTouch) return;
    if (hasSeenTour()) return;
    // Defer one tick so the chrome (anchors) has mounted + laid out.
    const id = window.setTimeout(() => startTour(), 800);
    return () => window.clearTimeout(id);
  }, [isTouch, hasSeenTour, startTour]);

  // Publish an on-screen snapshot of the Designer (open project + open files +
  // the active file) so the voice ("BRIDGE") and Pi agents can actually SEE the
  // IDE state. GATED on the Designer view being active (all shells stay mounted
  // under the display:none toggle; only the visible one owns the shared
  // snapshot; `viewMode` in the deps re-publishes on switching back). Benign
  // editor control (open a file, run a build) stays on the existing Pi IDE tools
  // + confirm-gated deploy, not on the vibe:module-action bus here. (ghl/App.tsx.)
  const viewMode = useUIStore((s) => s.viewMode);
  const currentProject = useProjectStore((s) => s.currentProject);
  const openFiles = useProjectStore((s) => s.openFiles);
  const activeFileId = useProjectStore((s) => s.activeFileId);
  const setSpaContext = useSpaContextStore((s) => s.setSpaContext);
  useEffect(() => {
    if (viewMode !== 'designer') return;
    setSpaContext(buildDesignerSnapshot(currentProject, openFiles, activeFileId));
  }, [viewMode, currentProject, openFiles, activeFileId, setSpaContext]);

  // Materialize each panel JSX element ONCE per render — single
  // instantiation discipline. Each panel is referenced from exactly ONE
  // place in the rendered tree (either DesktopPanelGroup or
  // MobileStackedShell, never both simultaneously).
  //
  // "Pi chat on all tabs" P1 (2026-07-03): `chatNode` is now referenced ONLY by
  // the mobile branch. On desktop the single chat instance lives in PiChatDock
  // (App-level overlay) — NOT in DesktopPanelGroup — so it is reachable on every
  // view. Desktop + mobile are mutually exclusive (isTouch locked), so there is
  // still exactly one <ChatPanel> mounted per session.
  const filesNode = <FilesPanel />;
  const editorNode = <EditorPanel />;
  const previewNode = <PreviewPanel />;
  const chatNode = <ChatPanel />;
  const infoNode = <InfoPanel />;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text">
      {/* TopBar hoisted to App level (2026-07-03) — voice strip stays here. */}
      <ResizableVoicePanel />
      {isTouch ? (
        <>
          <MobileStackedShell
            editor={editorNode}
            preview={previewNode}
            chat={chatNode}
            info={infoNode}
          />
          {/* 1.128.0 Phase 2e: FilesDrawer mounts as sibling. filesNode
              flows here on touch OR to DesktopPanelGroup on desktop —
              never both (isTouch locked per session per useIsTouch). */}
          <FilesDrawer>{filesNode}</FilesDrawer>
        </>
      ) : (
        <DesktopPanelGroup
          files={filesNode}
          editor={editorNode}
          preview={previewNode}
          info={infoNode}
        />
      )}
      {/* 2026-06-27 — help overlays (guided tour + help-mode callouts/cards). */}
      <HelpOverlay />
    </div>
  );
}

/**
 * DesktopPanelGroup — the existing react-resizable-panels tree extracted
 * from AppShell's return. Structurally byte-identical to the pre-Phase-2d
 * AppShell return for the desktop branch. Receives the 5 panel children
 * as ReactNode props so the parent AppShell can pre-materialize them.
 */
function DesktopPanelGroup({
  files,
  editor,
  preview,
  info,
}: {
  files: React.ReactNode;
  editor: React.ReactNode;
  preview: React.ReactNode;
  info: React.ReactNode;
}) {
  // 2026-07-08 — editor pane collapse (window control in EditorPanel's header).
  // Driven imperatively through a Panel ref so the panel TREE stays structurally
  // identical (the mount-discipline rule above forbids conditionally rendering
  // panels). uiStore.editorCollapsed is the single source of truth; the effect
  // pushes state → panel, and onCollapse/onExpand pull user drags → state. Both
  // sides guard on the panel's actual isCollapsed() so they never ping-pong.
  const editorPanelRef = useRef<ImperativePanelHandle | null>(null);
  const editorCollapsed = useUIStore((s) => s.editorCollapsed);
  const setEditorCollapsed = useUIStore((s) => s.setEditorCollapsed);
  useEffect(() => {
    const panel = editorPanelRef.current;
    if (!panel) return;
    if (editorCollapsed && !panel.isCollapsed()) panel.collapse();
    else if (!editorCollapsed && panel.isCollapsed()) panel.expand();
  }, [editorCollapsed]);

  // 2026-07-11 — Info panel (Problems/Output/Terminal/Git) close/reopen. Same
  // imperative-ref discipline as the editor above: uiStore.infoCollapsed is the
  // single source of truth; the effect pushes state → panel and
  // onCollapse/onExpand pull user drags → state, each guarded on the panel's
  // actual isCollapsed() so they never ping-pong.
  const infoPanelRef = useRef<ImperativePanelHandle | null>(null);
  const infoCollapsed = useUIStore((s) => s.infoCollapsed);
  const setInfoCollapsed = useUIStore((s) => s.setInfoCollapsed);
  useEffect(() => {
    const panel = infoPanelRef.current;
    if (!panel) return;
    if (infoCollapsed && !panel.isCollapsed()) panel.collapse();
    else if (!infoCollapsed && panel.isCollapsed()) panel.expand();
  }, [infoCollapsed]);

  return (
    <PanelGroup
      direction="horizontal"
      className="flex-1"
    >
      {/* Files Panel */}
      <Panel
        id="files"
        defaultSize={PANELS.files.default}
        minSize={PANELS.files.min}
        maxSize={PANELS.files.max}
        className="bg-vibe-bg-secondary"
      >
        {files}
      </Panel>

      <ResizeHandle />

      {/* Center: Editor/Preview + Info */}
      <Panel
        id="center"
        defaultSize={PANELS.center.default}
        minSize={PANELS.center.min}
      >
        <PanelGroup direction="vertical">
          {/* Editor + Preview (horizontal split) */}
          <Panel
            id="editor-preview"
            defaultSize={PANELS.editorPreview.default}
            minSize={PANELS.editorPreview.min}
          >
            <PanelGroup direction="horizontal">
              <Panel
                id="editor"
                ref={editorPanelRef}
                collapsible
                collapsedSize={0}
                defaultSize={PANELS.editor.default}
                minSize={PANELS.editor.min}
                onCollapse={() => {
                  if (!useUIStore.getState().editorCollapsed) setEditorCollapsed(true);
                }}
                onExpand={() => {
                  if (useUIStore.getState().editorCollapsed) setEditorCollapsed(false);
                }}
                className="bg-vibe-bg"
              >
                {editor}
              </Panel>
              <ResizeHandle />
              <Panel
                id="preview"
                defaultSize={PANELS.preview.default}
                minSize={PANELS.preview.min}
                className="bg-vibe-bg-secondary"
              >
                {preview}
              </Panel>
            </PanelGroup>
          </Panel>

          <ResizeHandle direction="horizontal" />

          {/* Info Panel — collapsible so its X / chevron can close it and the
              preview toolbar's "Show panel" can reopen it (2026-07-11). */}
          <Panel
            id="info"
            ref={infoPanelRef}
            collapsible
            collapsedSize={0}
            defaultSize={PANELS.info.default}
            minSize={PANELS.info.min}
            maxSize={PANELS.info.max}
            onCollapse={() => {
              if (!useUIStore.getState().infoCollapsed) setInfoCollapsed(true);
            }}
            onExpand={() => {
              if (useUIStore.getState().infoCollapsed) setInfoCollapsed(false);
            }}
            className="bg-vibe-bg-secondary"
          >
            {info}
          </Panel>
        </PanelGroup>
      </Panel>
      {/* "Pi chat on all tabs" P1 (2026-07-03): the Chat Panel that used to sit
          here (rightmost, 28%) is hoisted to PiChatDock (App-level overlay). */}
    </PanelGroup>
  );
}
